import type {ProductVariant, ProductWithVariants} from '@/src/core/domain/entities/product'
import {
  ProductAttribute,
  ProductAttributeValue,
  ProductPrice,
  sortAttributeValues,
} from '@/src/core/domain/entities/product'

import first from 'lodash/first'
import keys from 'lodash/keys'
import {action, computed, makeObservable, observable, reaction} from 'mobx'
import {BasketStoreInterface} from '@/src/presentation/stores/basket/basket.store.interface'

export class ProductVariantsStore {
  @observable
  productData!: ProductWithVariants
  @observable
  variant: Nullable<ProductVariant> = null

  constructor(productData: ProductWithVariants) {
    this.productData = {...productData}
    this.productData.attributes = [...(productData.attributes ?? [])]
    this.productData.variants = [...(productData.variants ?? [])]

    makeObservable(this)

    this.subscribeReactions()
  }

  subscribeReactions() {
    reaction(
      () => this.variant,
      () => {
        this.checkAvailableAttributes()
      },
    )
  }

  @computed
  get inBasket() {
    return (basket: BasketStoreInterface) => {
      return basket.haveProduct(this.productData)
    }
  }

  @computed
  get variants() {
    return this.productData.variants!
  }

  @computed
  get availableVariants() {
    return this.variants.filter(variant => variant.available)
  }

  @computed
  get availableAttributes() {
    return this.attributes.filter(attribute =>
      this.availableVariants.some(variant => Object.prototype.hasOwnProperty.call(variant.attributes, attribute.code)),
    )
  }

  getAttributeValues(attributeCode: string) {
    const attribute = this.attributes.find(attr => attr.code === attributeCode)
    if (!attribute) return []

    return sortAttributeValues(
      attribute.values.map(attributeValue => ({
        ...attributeValue,
        available: this.availableVariants.some(
          variant =>
            Object.prototype.hasOwnProperty.call(variant.attributes, attributeCode) &&
            variant.attributes[attributeCode] === attributeValue.value,
        ),
        disabled: !this.availableVariants.some(
          variant =>
            Object.prototype.hasOwnProperty.call(variant.attributes, attributeCode) &&
            variant.attributes[attributeCode] === attributeValue.value,
        ),
      })),
    )
  }

  @computed
  get attributes() {
    return this.productData.attributes!
  }

  @computed
  get haveActiveAttributes() {
    return (
      this.attributes.find(attribute => {
        return attribute.values.find(value => !!value.active) !== undefined
      }) !== undefined
    )
  }

  @computed
  get selectedAttributesMap() {
    return this.attributes.reduce(
      (acc, attribute) => {
        for (const attributeValueInfo of attribute.values) {
          if (attributeValueInfo.active) {
            acc[attribute.code] = attributeValueInfo.value
          }
        }
        return acc
      },
      {} as Record<any, any>,
    )
  }

  @computed
  get price(): Nullable<ProductPrice> {
    return this.variant?.price ?? null
  }

  @action.bound
  resolveInitialVariant() {
    if (this.variant) {
      if (this.availableVariants.find(variant => variant.id === this.variant!.id) === undefined) {
        throw Error(
          `Варианта \`${this.variant.id}\` нет в товаре \`${this.productData.name}\` [${this.productData.id}]`,
        )
      }

      return this.variant
    }

    const defaultVariant =
      (this.availableVariants.length > 0 ? first(this.availableVariants) : null) ?? null

    if (defaultVariant) {
      this._setSelectedAttributesByVariant(defaultVariant)
    }
  }

  @action.bound
  _setSelectedAttributesByVariant(variant: ProductVariant) {
    const attributes = variant.attributes ?? {}

    for (const attributeKey in attributes) {
      this.setSelectedAttribute(attributeKey, attributes[attributeKey]!)
    }
  }

  @action.bound
  setSelectedAttribute(code: ProductAttribute['code'], value: ProductAttributeValue['value']) {
    const attribute = this.productData.attributes!.find(attribute => attribute.code === code)
    if (!attribute) throw Error(`Аттрибут \`${code}\` товара \`${this.productData.name}\` не найден`)

    const attributeValue = attribute.values.find(attributeValue => attributeValue.value === value)

    if (!attributeValue) throw Error(`Значение \`${value}\` аттрибута \`${code}\` не найдено`)

    attribute.values.forEach(v => (v.active = false))
    attributeValue.active = true

    attribute.values = [...attribute.values]

    this.productData.attributes = [...this.productData.attributes!]
    this.productData = {...this.productData}

    this.checkActiveVariant()
    this.checkAvailableAttributes()
  }

  @action.bound
  checkAvailableAttributes() {
    let needUpdate = false

    // 1. Пересчитываем disabled
    if (this._updateDisabledValues()) needUpdate = true

    // 2. Пересчитываем active
    if (this._updateActiveValues()) needUpdate = true

    if (needUpdate) {
      this.productData = {
        ...this.productData,
        variants: [...this.variants],
        attributes: [...this.attributes],
      }
    }
  }

  /**
   * Проверяет вхождение атрибутов из вариантов на соответствие текущим выбранным (не полное соответствие).
   * notPossibleAttributeValues необходим, чтобы деактивировать те значения атрибутов,
   * которых нет ни в каких вариантах с учетом текущего выбора
   * @param selection - выбранные атрибуты
   * @private
   */
  private _variantMatchesSelection(selection: Record<string, any>): [boolean, Record<any, any>] {
    const notPossibleAttributeValues: Record<string, any[]> = {}

    const isPossible = this.variants.some(variant => {
      return Object.entries(selection).every(([code, val]) => {
        if (!Object.prototype.hasOwnProperty.call(variant.attributes, code)) {
          if (!notPossibleAttributeValues[code]) {
            notPossibleAttributeValues[code] = []
          }
          notPossibleAttributeValues[code].push(val)

          return true
        }
        return variant.attributes[code] === val
      })
    })

    return [isPossible, notPossibleAttributeValues]
  }

  /**
   * Обновляет disabled для всех значений атрибутов
   */
  private _updateDisabledValues(): boolean {
    let updated = false
    const selected = this.selectedAttributesMap

    for (const attribute of this.attributes) {
      const hasActiveValue = attribute.values.some(v => v.active)

      for (const value of attribute.values) {
        const hypothetical = hasActiveValue
          ? {...selected, [attribute.code]: value.value}
          : {...selected, [attribute.code]: value.value, ...(delete selected[attribute.code] && {})}

        const [isPossible, notPossibleAttributes] = this._variantMatchesSelection(hypothetical)

        let shouldBeDisabled = !isPossible

        if (!shouldBeDisabled) {
          if (notPossibleAttributes[attribute.code] && notPossibleAttributes[attribute.code].includes(value.value)) {
            shouldBeDisabled = true
          }
        }

        if (value.disabled !== shouldBeDisabled) {
          value.disabled = shouldBeDisabled
          updated = true
        }
      }
    }

    return updated
  }

  /**
   * Обновляет active для значений в зависимости от selectedAttributesMap
   */
  private _updateActiveValues(): boolean {
    let updated = false

    for (const attribute of this.attributes) {
      for (const value of attribute.values) {
        const shouldBeActive = this.selectedAttributesMap[attribute.code] === value.value && !value.disabled
        if (value.active !== shouldBeActive) {
          value.active = shouldBeActive
          updated = true
        }
      }
    }

    return updated
  }

  @action.bound
  selectVariant(variant: Nullable<ProductVariant>) {
    if (variant === null) {
      this.unselectVariant()
    } else {
      this.variant = variant
    }
  }

  @action.bound
  unselectVariant() {
    for (const attribute of this.attributes) {
      for (const attributeValue of attribute.values) {
        attributeValue.disabled = false
        attributeValue.active = false
      }
    }

    this.productData = {
      ...this.productData,
      attributes: this.attributes,
    }
  }

  @action.bound
  checkActiveVariant() {
    let selectedVariant = this.variant

    for (const variant of this.variants) {
      if (this._variantSelected(variant)) {
        selectedVariant = variant
      }
    }

    this.selectVariant(selectedVariant)
  }

  _variantSelected(variant: ProductVariant) {
    let activeCounter = 0

    for (const attributeCode of keys(this.selectedAttributesMap)) {
      if (variant.attributes[attributeCode] === this.selectedAttributesMap[attributeCode]) {
        activeCounter++
      }
    }

    return activeCounter >= keys(variant.attributes).length
  }
}
